// Which of the day's sounds the song on the River is playing right now: its drum kit, the
// instrument choices it uses (main sounds, alternates via `variants`, extra lanes) and the atmos
// kit. Empty during a break or the dawn bar, so the panel's highlights fade out between songs.
// Read ahead and switched by timers at the songs' known start and end bars (no polling).
import { useEffect, useState } from "react";
import {
  everyEvent,
  instrumentChoices,
  isAtmosPad,
  isInstrumentSlot,
  laneSlot,
  laneVariant,
  loopVariant,
  NUM_PADS,
  NUM_SOUNDS,
  SLOT_ROLE,
  SONG_BARS,
  type LoopData,
  type PeriodDef,
} from "@eb/shared";
import { clock } from "../stream";
import type { RiverPlayer } from "./player";

/** Ids of what `loop` plays: "kit", "atmos" and the preset ids of its instrument sounds. */
export function soundsOf(def: PeriodDef, loop: LoopData): Set<string> {
  const used = new Set<string>();
  for (const e of everyEvent(loop)) {
    if (e.pad < NUM_PADS) used.add("kit");
    else if (isAtmosPad(e.pad)) used.add("atmos");
    else if (isInstrumentSlot(e.pad)) {
      const slot = laneSlot(e.pad);
      const variant = e.pad >= NUM_SOUNDS ? laneVariant(e.pad) : loopVariant(loop, slot);
      const choices = instrumentChoices(def, SLOT_ROLE[slot]);
      used.add(choices[variant] ?? choices[0]);
    }
  }
  return used;
}

/**
 * The sounds of the River's current song (empty between songs). No polling: songs start and end
 * on known bars of the world clock, so it reads the next song ahead and sets one timer for each
 * hand-over — the set is locked when the song starts and cleared when its break begins.
 */
export function useUsedSounds(player: RiverPlayer | null, def: PeriodDef | null): Set<string> {
  const [used, setUsed] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!player || !def) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const sounds = new Map<string, Set<string>>();
    /** Read a song's sounds ahead (cached by slot). */
    const prepare = (day: number, slot: number) => {
      const key = `${day}:${slot}`;
      if (day !== def.day || sounds.has(key)) return;
      sounds.set(key, new Set());
      void player
        .lineup(day)
        .then((l) => player.loop(l.entries[slot].loopId))
        .then((loop?: LoopData) => {
          if (!alive || !loop) return;
          const set = soundsOf(def, loop);
          sounds.set(key, set);
          // Already playing (a song read at its start): show it now.
          const c = clock.barContent(clock.barAt(player.worldNow(), player.epochMs()));
          if (c.kind === "loop" && `${c.day}:${c.slot}` === key) setUsed(set);
        })
        .catch(() => undefined);
    };
    const step = () => {
      if (!alive) return;
      const epoch = player.epochMs();
      const now = player.worldNow();
      const bar = clock.barAt(now, epoch);
      const c = clock.barContent(bar);
      let next: number;
      if (c.kind === "loop") {
        // Show what was read ahead at once, then read it again from the lineup as it stands now
        // (an encore may have changed the slot after it was read ahead).
        const key = `${c.day}:${c.slot}`;
        setUsed(sounds.get(key) ?? new Set());
        sounds.delete(key);
        prepare(c.day, c.slot);
        prepare(c.day, c.slot + 1); // the next song, ahead of its start
        next = bar - c.loopBar + SONG_BARS; // its break
      } else {
        setUsed(new Set());
        const after = clock.barContent(bar + 1);
        if (after.kind === "loop") prepare(after.day, after.slot);
        next = bar + 1;
      }
      const at = epoch + next * clock.MS_PER_BAR;
      timer = setTimeout(step, Math.max(20, at - player.worldNow() + 15));
    };
    step();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [player, def]);
  return used;
}
